import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';
import CheckInSection from '../../components/CheckInSection/CheckInSection';
import PointsHistory from '../../components/PointsHistory/PointsHistory';
import OrderList from '../../components/OrderList/OrderList';
import InvitationSection from '../../components/InvitationSection/InvitationSection';
import ChangePassword from '../../components/ChangePassword/ChangePassword';
import styles from './Profile.module.css';

const Profile = () => {
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state?.tab]);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      // Wait for auth context to finish loading
      if (authLoading) {
        return;
      }

      try {
        console.log('Profile: Checking authentication...');
        if (isAuthenticated()) {
          console.log('Profile: User is authenticated, fetching profile...');
          await fetchUserProfile();
        } else {
          console.log('Profile: User is not authenticated');
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setLoading(false);
      }
    };

    checkAuthAndFetch();

    // Fallback timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('Profile: Loading timeout reached, stopping loading');
      setLoading(false);
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeout);
  }, [authLoading, isAuthenticated]);

  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInSuccess = (reward) => {
    // 更新用户信息
    setUserProfile(prev => ({
      ...prev,
      coinBalance: prev.coinBalance + reward.coinBalance,
      checkInStreak: prev.checkInStreak + 1,
      totalCheckIns: prev.totalCheckIns + 1,
      lastCheckIn: new Date().toISOString()
    }));
  };

  const handleChangePasswordSuccess = (msg) => {
    setMessage(msg);
    // 成功消息显示更长时间
    setTimeout(() => setMessage(''), 5000);
  };

  const handleChangePasswordClose = () => {
    setShowChangePassword(false);
  };

  if (!isAuthenticated()) {
    return (
      <div className={styles.profilePage}>
        <div className="container">
          <div className={styles.notLoggedIn}>
            <h2>请先登录</h2>
            <p>您需要登录后才能查看个人中心</p>
            <a href="/login" className="btn btn-primary">
              立即登录
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className={styles.profilePage}>
        <div className="container">
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  const profileData = userProfile ? {
    ...userProfile,
    nickname: userProfile.nickname || user?.nickname || '用户',
    email: userProfile.email || user?.email || '',
    avatar: userProfile.avatar || user?.avatar || '',
    coinBalance: userProfile.coinBalance || 0,
    checkInStreak: userProfile.checkInStreak || 0,
    totalCheckIns: userProfile.totalCheckIns || 0,
    inviteCode: userProfile.inviteCode || '',
    inviteCount: userProfile.inviteCount || 0,
    memberLevel: userProfile.memberLevel || '普通用户',
    joinDate: userProfile.joinDate || userProfile.createdAt || user?.createdAt || new Date().toISOString()
  } : {
    nickname: user?.nickname || '用户',
    email: user?.email || '',
    avatar: user?.avatar || '',
    coinBalance: 0,
    checkInStreak: 0,
    totalCheckIns: 0,
    inviteCode: '',
    inviteCount: 0,
    memberLevel: '普通用户',
    joinDate: user?.createdAt || new Date().toISOString()
  };

  // 安全处理加入时间
  const formatJoinDate = (dateStr) => {
    if (!dateStr) return '最近加入';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '最近加入';
      return date.toLocaleDateString('zh-CN');
    } catch {
      return '最近加入';
    }
  };

  const tabItems = [
    { id: 'overview', name: '概览', icon: '👤' },
    { id: 'checkin', name: '签到', icon: '📅' },
    { id: 'points', name: '金币流水', icon: '💰' },
    // { id: 'orders', name: '订单列表', icon: '📦' },
    { id: 'invitation', name: '邀请管理', icon: '🎁' }
  ];

  return (
    <div className={styles.profilePage}>
      <div className="container">
        <div className={styles.profileHeader}>
          <div className={styles.userInfo}>
            <div className={styles.avatarSection}>
              {profileData.avatar ? (
                <img src={profileData.avatar} alt="头像" className={styles.userAvatar} />
              ) : (
                <div className={styles.defaultAvatar}>
                  {profileData.nickname.charAt(0).toUpperCase()}
                </div>
              )}
              {/* <button className={styles.uploadAvatarBtn}>
                更换头像
              </button> */}
            </div>

            <div className={styles.basicInfo}>
              <h1 className={styles.userNickname}>{profileData.nickname}</h1>
              <p className={styles.userEmail}>{profileData.email}</p>
              <div className={styles.memberBadge}>
                <span className={styles.badge}>{profileData.memberLevel}</span>
                <span className={styles.joinDate}>
                  加入时间：{formatJoinDate(profileData.joinDate)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.userStats}>
            <div className={styles.statItem}>
              <div className={styles.statIcon}>💰</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{profileData.coinBalance}</span>
                <span className={styles.statLabel}>金币</span>
              </div>
            </div>
            {/* <div className={styles.statItem}>
              <div className={styles.statIcon}>🔥</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{profileData.checkInStreak}</span>
                <span className={styles.statLabel}>连续签到</span>
              </div>
            </div> */}
            <div className={styles.statItem}>
              <div className={styles.statIcon}>📅</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{profileData.totalCheckIns}</span>
                <span className={styles.statLabel}>累计签到</span>
              </div>
            </div>
            {/* <div className={styles.statItem}>
              <div className={styles.statIcon}>👥</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{profileData.inviteCount}</span>
                <span className={styles.statLabel}>邀请用户</span>
              </div>
            </div> */}
          </div>
        </div>

        <div className={styles.profileContent}>
          <div className={styles.tabNavigation}>
            {tabItems.map(tab => (
              <button
                key={tab.id}
                className={`${styles.tabItem} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className={styles.tabIcon}>{tab.icon}</span>
                <span className={styles.tabName}>{tab.name}</span>
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'overview' && (
              <div className={styles.overviewSection}>
                {message && (
                  <div className={styles.successMessage}>
                    {message}
                  </div>
                )}
                <div className={styles.overviewGrid}>
                  <div className={styles.overviewCard}>
                    <h3>账户安全</h3>
                    <div className={styles.securityItems}>
                      {/* <div className={styles.securityItem}>
                        <span>邮箱验证</span>
                        <span className={`${styles.status} ${styles.verified}`}>已验证</span>
                      </div> */}
                      <div className={styles.securityItem}>
                        <span>登录密码</span>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowChangePassword(true)}
                        >
                          修改
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* <div className={styles.overviewCard}>
                    <h3>使用统计</h3>
                    <div className={styles.usageStats}>
                      <div className={styles.usageItem}>
                        <span>今日搜索</span>
                        <span className={styles.value}>12 次</span>
                      </div>
                      <div className={styles.usageItem}>
                        <span>收藏资源</span>
                        <span className={styles.value}>56 个</span>
                      </div>
                      <div className={styles.usageItem}>
                        <span>下载次数</span>
                        <span className={styles.value}>128 次</span>
                      </div>
                    </div>
                  </div> */}

                  {/* <div className={styles.overviewCard}>
                    <h3>会员权益</h3>
                    <div className={styles.memberBenefits}>
                      <div className={styles.benefitItem}>
                        <span className={styles.benefitIcon}>✅</span>
                        <span>无限制搜索</span>
                      </div>
                      <div className={styles.benefitItem}>
                        <span className={styles.benefitIcon}>✅</span>
                        <span>查看网盘链接</span>
                      </div>
                      <div className={styles.benefitItem}>
                        <span className={styles.benefitIcon}>✅</span>
                        <span>收藏资源</span>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            )}

            {activeTab === 'checkin' && (
              <CheckInSection
                userProfile={profileData}
                onCheckInSuccess={handleCheckInSuccess}
              />
            )}

            {activeTab === 'points' && (
              <PointsHistory />
            )}

            {/* {activeTab === 'orders' && (
              <OrderList />
            )} */}

            {activeTab === 'invitation' && (
              <InvitationSection />
            )}
          </div>
        </div>

        {/* 修改密码弹窗 */}
        {showChangePassword && (
          <ChangePassword
            onClose={handleChangePasswordClose}
            onSuccess={handleChangePasswordSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default Profile;